import { useNavigate } from 'react-router-dom'
import { ArrowRight, GraduationCap } from 'lucide-react'
import { useComponentImpression, analyticsService } from '@meiuca/analytics'
import { TrainingCenterCard } from '../TrainingCenterCard'
import type { TrainingCenterListItem } from '@/types/trainingCenter'

interface TrainingCenterSectionProps {
  /** Centros vindos do payload da home — a seção não busca nada sozinha. */
  centers: TrainingCenterListItem[]
}

/**
 * Entrada da vitrine de centros na home.
 *
 * Assume duas formas conforme o que existe para mostrar: com centros
 * publicados vira carrossel de cards; sem nenhum, vira uma faixa que explica o
 * que são e leva para a busca. A faixa importa justamente no começo — a vitrine
 * é opt-in e nasce vazia, então um carrossel condicional simplesmente não
 * apareceria no lançamento, que é quando os primeiros centros mais precisam ser
 * encontrados.
 *
 * Segue o mesmo arranjo que a home já usa entre `ArenaCarousel` e
 * `SuggestArenaEmptyState`.
 */
export function TrainingCenterSection({ centers }: TrainingCenterSectionProps) {
  const navigate = useNavigate()

  const impressionRef = useComponentImpression({
    componentType: 'training_center_section',
    componentKey: 'home-training-centers'
  })

  const goToSearch = (from: 'carousel' | 'banner') => {
    analyticsService.track('ct_home_section_clicked', { from, centers_count: centers.length })
    navigate('/cts/search')
  }

  if (centers.length === 0) {
    return (
      <div ref={impressionRef} className="max-w-7xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => goToSearch('banner')}
          className="flex w-full items-center gap-4 rounded-xl border border-border-light bg-background-white p-5 text-left transition-shadow hover:shadow-md"
        >
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-primary/10">
            <GraduationCap className="h-6 w-6 text-brand-primary" />
          </span>

          <span className="flex-1">
            <span className="block font-semibold text-text-primary">
              Quer treinar com professor?
            </span>
            <span className="block text-sm text-text-secondary">
              Veja centros de treinamento, horários das turmas e fale direto no WhatsApp.
            </span>
          </span>

          <ArrowRight className="h-5 w-5 flex-shrink-0 text-text-tertiary" />
        </button>
      </div>
    )
  }

  return (
    <div ref={impressionRef} className="max-w-7xl mx-auto px-4 py-6">
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">Centros de treinamento</h2>
          <button
            type="button"
            onClick={() => goToSearch('carousel')}
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Ver todos
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
          {centers.map((center, index) => (
            <TrainingCenterCard
              key={center.id}
              center={center}
              className="w-[280px] flex-shrink-0 snap-start"
              onClick={() => {
                analyticsService.track('ct_card_clicked', {
                  ct_id: center.id,
                  position: index,
                  is_remote: center.offersRemoteConsulting,
                  component_type: 'training_center_carousel'
                })
                navigate(`/ct/${center.id}`)
              }}
            />
          ))}
        </div>
      </section>
    </div>
  )
}
